/**
 * All Projects Page Component
 * Reusable projects listing page with create/edit functionality
 * Used by: agencies, construction, architects, consulting, IT, photographers, events
 */

import { useState } from 'react';
import { useIndustryProjects } from '@/hooks/industry';
import { CompanyType } from '@/types/company';
import { IndustryProject, CreateProjectInput } from '@/types/industry';
import { ProjectList } from '../shared/ProjectList';
import { ProjectForm } from '../shared/ProjectForm';
import { Button } from '@/components/ui/button';
import { Plus, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface AllProjectsPageProps {
  domain: CompanyType;
  title: string;
  description: string;
  createButtonLabel?: string;
}

export function AllProjectsPage({
  domain,
  title,
  description,
  createButtonLabel = 'Novo Projeto',
}: AllProjectsPageProps) {
  const { projects, isLoading, createProject, updateProject } = useIndustryProjects({ domain });
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<IndustryProject | undefined>();

  // Fetch clients for the current company
  const { data: company } = useQuery({
    queryKey: ['current_company'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data } = await supabase
        .from('companies')
        .select('*')
        .eq('user_id', user.id)
        .single();

      return data;
    },
  });

  const { data: clients = [] } = useQuery({
    queryKey: ['clients', company?.id],
    queryFn: async () => {
      if (!company) return [];

      const { data } = await supabase
        .from('clients')
        .select('id, name')
        .eq('company_id', company.id)
        .eq('is_active', true)
        .order('name');

      return data || [];
    },
    enabled: !!company,
  });

  // Create client map for ProjectList
  const clientMap = clients.reduce((acc, client) => {
    acc[client.id] = client.name;
    return acc;
  }, {} as Record<number, string>);

  const handleCreate = async (data: CreateProjectInput) => {
    try {
      await createProject(data);
      toast.success('Projeto criado com sucesso!');
      setIsCreateDialogOpen(false);
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error('Erro ao criar projeto');
    }
  };

  const handleUpdate = async (data: CreateProjectInput) => {
    if (!selectedProject) return;

    try {
      await updateProject({
        id: selectedProject.id,
        ...data,
      });
      toast.success('Projeto atualizado com sucesso!');
      setIsEditDialogOpen(false);
      setSelectedProject(undefined);
    } catch (error) {
      console.error('Error updating project:', error);
      toast.error('Erro ao atualizar projeto');
    }
  };

  const handleEdit = (project: IndustryProject) => {
    setSelectedProject(project);
    setIsEditDialogOpen(true);
  };

  const handleView = (project: IndustryProject) => {
    // For now, just open edit dialog
    // In future, could show a read-only view
    handleEdit(project);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{title}</h1>
          <p className="text-muted-foreground">{description}</p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {createButtonLabel}
        </Button>
      </div>

      {/* Projects List */}
      <ProjectList
        projects={projects || []}
        isLoading={isLoading}
        onEdit={handleEdit}
        onView={handleView}
        clientMap={clientMap}
        showProfitability={true}
        pageSize={20}
        domain={domain}
      />

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>{createButtonLabel}</DialogTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsCreateDialogOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>
          <ProjectForm
            domain={domain}
            clients={clients}
            onSubmit={handleCreate}
            onCancel={() => setIsCreateDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Editar Projeto</DialogTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setIsEditDialogOpen(false);
                  setSelectedProject(undefined);
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>
          {selectedProject && (
            <ProjectForm
              domain={domain}
              project={selectedProject}
              clients={clients}
              onSubmit={handleUpdate}
              onCancel={() => {
                setIsEditDialogOpen(false);
                setSelectedProject(undefined);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
